import { Fragment, useMemo, useState, useEffect } from 'react';
import { isTypingTarget } from '../keyboard';
import { useKeys } from '../keyScope';
import { useMedia, PANES_STACK } from '../media';
import { minuteLabel, spanLabel } from '../durations';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getDayPlan,
  getGoalTree,
  scheduleTask,
  moveBlock,
  deleteBlock,
  completeTask,
  logHabit,
  undoHabitLog,
  getFreeSlots,
  bulkUpdateTasks,
  ApiError,
  type Task,
  type TimelineEvent,
  type Conflict,
  type DayHabit,
  type DayBudget,
  type FreeSlot,
  listProjects,
  type GoalTreeNode,
  listLabels,
  verdictTone,
} from '../api';

import {
  START_HOUR,
  END_HOUR,
  PX_PER_HOUR,
  SNAP_MINUTES,
  visibleWindow,
  nowOffset,
} from '../timelineLayout';
import { layoutOverlaps } from '../overlapLayout';
import { distinctSlots } from '../slotChoices';
import { goalChipsFor } from '../goalFocus';
import { TemplateQuickAdd } from './TemplateQuickAdd';
import { CarriedOver } from './CarriedOver';
import { WaitingRail } from './WaitingOn';
import { LogHabit } from './LogHabit';
import { habitFigure } from '../habitFigure';
import { TaskStatusControl, TaskProgressMark } from './TaskStatusControl';
import { PriorityMark } from './StatusBadge';
import { STATUS_VERB, dueLook, dueClass, dueTitle } from '../taskVocab';
import { readableDay, readableInstant } from '../dateLabels';
import { PeriodStrip, StripLegend } from './PeriodStrip';
import { periodIsOver, runFigure, verdictIsMet, verdictWord } from '../habitVocab';
import { byUndone, readOrder, useLogHold } from '../habitOrder';
import { PeriodNav } from './PeriodNav';
import { goalColorMap, resolveTaskColor, nearestColored } from '../colors';
import { TaskMenu, useTaskMenu } from './TaskMenu';
import { Capture } from './Capture';
import { shiftDay, todayInZone } from '../timezones';
import { GoalsToday } from './GoalsToday';
import { QueryState } from './QueryState';
import { WorkElsewhere } from './WorkElsewhere';
import { ParentLink } from './ParentLink';
import { SubtaskPill } from './SubtaskPill';
import { ChecklistLine } from './ChecklistLine';
import { TodayWip, useInProgressRows } from './WipNotice';
import { ReadbackSlot, useCommandReadback } from './CommandReadback';
import {
  CAPTURED_BATCH,
  elsewhereText,
  groupShortlist,
  inProgressElsewhere,
} from '../shortlistGroups';

/**
 * Today in the *user's* zone, not UTC.
 *
 * `new Date().toISOString()` gives the UTC date, so for anyone east of Greenwich the
 * Today screen showed yesterday after local 22:00 — the same class of error the habit
 * period maths exists to avoid, reintroduced in the UI. Only running it in a real
 * browser at 01:00 CEST exposed it.
 */
const todayIso = todayInZone;

/** Below this a filter is chrome; above it the shortlist is long enough to hunt in. */
const SHORTLIST_FILTER_AT = 8;

/**
 * How long a *click* on the grid means, when no drag said otherwise.
 *
 * Half an hour: long enough to be a real appointment and short enough that nobody feels the app
 * decided their afternoon for them. The edge can be dragged afterwards.
 */
const DEFAULT_DRAWN_MINUTES = 30;

/** "1st", "2nd", "3rd" — for counting a template's preferred windows out loud. */
function ordinal(n: number): string {
  const rest = n % 100;
  if (rest >= 11 && rest <= 13) return `${n}th`;
  return `${n}${['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`;
}

/**
 * A day's blocks, gathered per task and put in time order.
 *
 * Separate from the component because it is the arithmetic, not the rendering, and because two
 * places need the same answer: which sitting a block is, and how much of a task the day already
 * accounts for.
 */
function groupBlocksByTask(
  events: readonly { id: string; taskId: string | null; startsAt: string; endsAt: string }[],
): Map<string, { id: string; minutes: number }[]> {
  const byTask = new Map<string, { id: string; start: number; minutes: number }[]>();
  for (const event of events) {
    if (!event.taskId) continue;
    const start = new Date(event.startsAt).getTime();
    const minutes = Math.round((new Date(event.endsAt).getTime() - start) / 60_000);
    byTask.set(event.taskId, [
      ...(byTask.get(event.taskId) ?? []),
      { id: event.id, start, minutes },
    ]);
  }
  return new Map(
    [...byTask].map(([taskId, blocks]) => [
      taskId,
      [...blocks].sort((a, b) => a.start - b.start).map(({ id, minutes }) => ({ id, minutes })),
    ]),
  );
}

const minutesFromDayStart = (iso: string, dayStart: string) =>
  (new Date(iso).getTime() - new Date(dayStart).getTime()) / 60_000;

const clockLabel = (iso: string, timezone: string) => readableInstant(iso, timezone, 'clock');

function ConflictNotice({
  conflicts,
  onDismiss,
}: {
  conflicts: Conflict[];
  onDismiss: () => void;
}) {
  if (conflicts.length === 0) return null;
  return (
    <p className="banner">
      Overlaps {conflicts.map((c) => c.title).join(', ')} — kept anyway.{' '}
      <button className="link" onClick={onDismiss}>
        dismiss
      </button>
    </p>
  );
}

/**
 * How much room the day has, against how much has been asked of it.
 *
 * The timeline shows the day's hours and nothing showed the day's room, so a person with five
 * hours between meetings shortlisted seven hours of work every morning and found out at six.
 *
 * Amber and never red (§4: warn, never block). `--bad` is reserved for something that has gone
 * wrong, and planning more than fits has not gone wrong — it is a thing worth knowing before the
 * afternoon rather than after it. Nothing here refuses a drag or hides a row.
 *
 * `unestimated` is said out loud beside the sum rather than folded into it. Measured across the
 * persona accounts on 2026-09-10, most of a real shortlist carries no estimate — 10 rows of 16 on
 * the planner, 43 of 48 on the manager, 22 of 22 on the minimalist — so a bare "2h 30m
 * shortlisted" would be a confident number describing a third of the list, and the bar would be
 * quietly reassuring about a day it has not measured.
 */
function DayBudgetBar({ budget, overflow }: { budget: DayBudget; overflow: number }) {
  const over = budget.shortlistedMinutes > budget.freeMinutes;
  /* Against the larger of the two, so the fill is a share of the whole bar in both directions:
     an under-committed day fills part of it, an over-committed one fills all of it and the
     amber is what says which. Guarded because a day with no free minutes left divides by zero. */
  const scale = Math.max(budget.freeMinutes, budget.shortlistedMinutes, 1);
  return (
    <div
      className={`day-budget ${over ? 'is-over' : ''}`}
      data-testid="day-budget"
      data-over={over ? 'true' : 'false'}
    >
      <div className="budget-track" aria-hidden="true">
        <span
          className="budget-fill"
          style={{ width: `${Math.round((budget.shortlistedMinutes / scale) * 100)}%` }}
        />
        {/*
          Where the room runs out.

          Without it the over-budget bar is a solid amber rule across the pane — full whichever way
          you are over, so it says "too much" and cannot say "by how much", which is the one thing
          a bar is better at than the sentence beneath it. On a day with room to spare the mark
          sits flush against the right-hand edge and reads as the end of the track, which is what
          it is.
        */}
        <span
          className="budget-room"
          style={{ left: `${Math.round((budget.freeMinutes / scale) * 100)}%` }}
        />
      </div>
      <p className="subtext budget-line">
        <strong>{spanLabel(budget.shortlistedMinutes)}</strong> shortlisted ·{' '}
        <strong>{spanLabel(budget.freeMinutes)}</strong> free
        {budget.unestimated > 0 && (
          <>
            {' · '}
            <span
              className="budget-unestimated"
              title="Counted as nothing, because nobody said how long they take. The figure on the left is a floor."
            >
              {budget.unestimated} with no estimate
            </span>
          </>
        )}
      </p>
      {/* The count in words for a screen reader, which has no bar to look at and no fold line to
          see: `.shortlist-fold` is `aria-hidden` because sighted readers get it from the rule. */}
      {overflow > 0 && (
        <p className="sr-only">{overflow} of these do not fit in the time that is left.</p>
      )}
    </div>
  );
}

export function Today({
  onOpenTask,
  onOpenNow,
  timezone,
  dayWindow,
  initialDate,
  onOpenProjectBoard,
  onOpenPage,
  onSeeLate,
}: {
  onOpenTask: (id: string) => void;
  /**
   * Open the doing screen, holding this block.
   *
   * The way in to `Now`, and the only one: §3 of the request puts it on this row rather than in
   * the nav, because the nav rehang is its own task (`01a08883-c0b8`) and blocked on this one.
   */
  onOpenNow: (blockId: string) => void;
  timezone: string;
  /** The user's preferred hours. The view still widens to fit what is scheduled. */
  dayWindow?: { startHour: number; endHour: number };
  /** The Tasks screen, already aimed at everything late — the late band's *See them*. */
  onSeeLate: () => void;
  /**
   * The day to open on, when somebody arrived here asking for one.
   *
   * Read once, into the initial state below — this screen's arrows own the date afterwards. The
   * caller remounts on a change (`App.tsx` keys on it) rather than expecting a later prop to be
   * picked up, so there is no second source of truth for which day is on screen.
   */
  initialDate?: string;
  /** Where the band above the day sends somebody who has no work of their own. */
  onOpenProjectBoard: (projectId: string) => void;
  /**
   * Open this day's page — the record of what happened, and the note about it.
   *
   * On the date row rather than in the nav, because the page is about *this* day and the date row
   * is the only control on the screen that says which day that is. A nav tab would have to carry
   * its own date and would then be a second opinion about which day you are looking at.
   */
  onOpenPage: (date: string) => void;
}) {
  const queryClient = useQueryClient();
  const [date, setDate] = useState(() => initialDate ?? todayIso(timezone));
  /*
    Which of the three panes a narrow screen is showing.

    Above 60rem all three sit side by side and this is ignored. Below it they stacked, which made
    the screen the product lives or dies by a 3735px scroll on a phone: the timeline began 1676px
    down and the habits pane at 2700px — seven screens — so logging the gym meant scrolling past
    a thousand pixels of timeline to reach it.
  */
  /*
    The phone opens on the shortlist, not the timeline.

    Below 60rem the three panes become tabs and only one is on screen. It opened on the timeline,
    which on a day nothing has been planned yet is an empty grid of hours — so the first thing the
    app showed on a phone was a blank screen, with the five things you might do one tap away and
    invisible. The shortlist is what the timeline is *populated from*, so it is also the right
    place to start when the day is empty.

    Only the initial pane. Choosing the timeline sticks for the session, and on a desktop all
    three are visible at once so this never applies.
  */
  const [pane, setPane] = useState<'shortlist' | 'timeline' | 'habits'>('shortlist');
  /* Whether the timeline is a different tab rather than a column beside this one — see the hint
     below, and `PANES_STACK`. */
  const panesStack = useMedia(PANES_STACK);
  /*
    ...unless there is nothing to plan and something to log.

    The reasoning above holds for an account with tasks. For one with 31 habits and no tasks at
    all it meant every launch opened on "Nothing waiting", one tap from the only pane that account
    uses — and the habits rail is the better habit surface in the product, compact rows with
    seven-day strips.

    Derived from the data rather than remembered, so nothing is stored that could later disagree
    with what is on screen; the segmented control always shows which pane you are on. Once you
    pick a pane yourself, this stops having an opinion.
  */
  const [paneChosen, setPaneChosen] = useState(false);
  const [conflicts, setConflicts] = useState<Conflict[]>([]);
  // The same menu the task list uses — the shortlist is where a task is furthest from its panel.
  const menu = useTaskMenu();
  /**
   * What is being dragged, and where it would land.
   *
   * `dataTransfer` is unreadable during `dragover` — the browser only exposes it on
   * drop — so the preview has to be driven from React state set at `dragstart`. The
   * *drop itself* still reads `dataTransfer`, which is what I-25 was about; this is only
   * what gets drawn on the way.
   */
  const [dragging, setDragging] = useState<{
    title: string;
    durationMinutes: number;
    taskId?: string;
    eventId?: string;
  } | null>(null);
  const [previewMinutes, setPreviewMinutes] = useState<number | null>(null);
  /**
   * The task we are looking for a slot for.
   *
   * Dragging needs aim: you have to know where 14:15 is, and hit it. Asking "where does this
   * fit" needs none — the day answers, and you click one of its answers. `/free-slots` was
   * built in Phase 4 and never called from anywhere, so the capability existed and the day
   * view could not reach it.
   */
  /*
    Drawing a new task straight onto the day.

    Two rules make this safe to have. A *click* selects a default-length slot and writes nothing; a
    *drag* sets the length. Neither commits until a title is confirmed, and Escape leaves nothing
    behind — a calendar that creates something every time you brush it is worse than one that
    cannot create anything at all.

    `drawing` is the gesture in flight; `pending` is the finished selection waiting for a title.
  */
  const [drawing, setDrawing] = useState<{ from: number; to: number } | null>(null);
  const [pending, setPending] = useState<{ startMinutes: number; durationMinutes: number } | null>(
    null,
  );

  const [fitting, setFitting] = useState<{
    /** Placing a shortlist task… */
    taskId?: string;
    /** …or moving a block already on the day. */
    eventId?: string;
    title: string;
    durationMinutes: number;
    /** Where the task came from, so its preferred windows can mark the offered slots. */
    templateId?: string | null;
  } | null>(null);

  /**
   * The block being edited without dragging.
   *
   * Dragging is a mouse gesture. HTML5 drag-and-drop does not fire on touch at all, so the
   * timeline — the central thing this application does — was unusable on a phone even after the
   * rest of the layout worked. The controls on a block are also 11px wide and its resize strip
   * 7px tall, which is fine with a cursor and hopeless with a thumb.
   *
   * Selecting a block puts its actions somewhere with room for them, which turns out to be
   * easier with a mouse too.
   */
  const [selectedBlock, setSelectedBlock] = useState<string | null>(null);

  const plan = useQuery({ queryKey: ['day', date], queryFn: () => getDayPlan(date) });

  /*
    Pick the opening pane from what the day actually holds. Runs when the plan first arrives and
    stops mattering the moment the reader chooses a pane themselves.
  */
  useEffect(() => {
    if (paneChosen || !plan.data) return;
    const nothingToPlan = plan.data.candidates.length === 0 && plan.data.tasks.length === 0;
    if (nothingToPlan && plan.data.habits.length > 0) setPane('habits');
  }, [plan.data, paneChosen]);

  /*
    How each task's day is divided.

    A task can hold several blocks — work that needs a break, or that genuinely has parts — so a
    block on its own no longer says whether the task is planned. `sittingOf` is which of the task's
    blocks each one is, in time order; `plannedByTask` is the total, which the shortlist uses to
    say how much is still unplanned.
  */
  const sittingOf = new Map<string, { index: number; total: number }>();
  const plannedByTask = new Map<string, number>();
  for (const [taskId, blocks] of groupBlocksByTask(plan.data?.events ?? [])) {
    plannedByTask.set(
      taskId,
      blocks.reduce((sum, b) => sum + b.minutes, 0),
    );
    // Only worth numbering when there is more than one: "1 of 1" is noise.
    if (blocks.length > 1) {
      blocks.forEach((b, i) => sittingOf.set(b.id, { index: i + 1, total: blocks.length }));
    }
  }

  /**
   * Goal titles, for the chip on each shortlist row.
   *
   * Shares its cache key with the goals screen, so this is not an extra fetch in practice. A
   * task's purpose belongs where you decide whether to do it today, not two screens away.
   */
  const goalTitles = useQuery({ queryKey: ['goals', 'tree'], queryFn: getGoalTree });
  // Needed only for colour: a row's colour comes from its project before its goal.
  const projectsForColor = useQuery({ queryKey: ['projects'], queryFn: listProjects });
  const labelsForColor = useQuery({ queryKey: ['labels'], queryFn: listLabels });
  const labelNameById = new Map((labelsForColor.data ?? []).map((l) => [l.id, l.name] as const));

  /*
    Task id → its goal's colour class, for the timeline.

    Built from the day plan's own tasks and candidates, which already carry `goalIds`, joined to
    the goal tree that is fetched here anyway. No extra request.
  */
  const goalClassById = useMemo(() => goalColorMap(goalTitles.data ?? []), [goalTitles.data]);

  /*
    The same colour chain the task list uses.

    The shortlist had none at all — it was the one list the Phase 59 resolver was never wired to,
    so a task shown teal on the Tasks screen was plain here. Timeline blocks did have colour, but
    through the Phase 40 goal-only path, so a task's block and its row could disagree.
  */
  const rowColor = useMemo(() => {
    const projects = projectsForColor.data ?? [];
    const labelById = new Map((labelsForColor.data ?? []).map((l) => [l.id, l] as const));
    const fromLabel = (ids: string[]) => {
      for (const id of ids) {
        const label = labelById.get(id);
        if (label?.color) return { color: label.color, from: label.name };
      }
      return null;
    };
    const byId = new Map(
      projects.map((p) => [p.id, { id: p.id, color: p.color, parentId: p.parentProjectId }]),
    );
    const names = new Map(projects.map((p) => [p.id, p.title]));
    const flat = (nodes: GoalTreeNode[]): GoalTreeNode[] =>
      nodes.flatMap((n) => [n, ...flat(n.children)]);
    const goalRaw = new Map(flat(goalTitles.data ?? []).map((n) => [n.goal.id, n.goal.color]));

    return (task: {
      color: string | null;
      projectId: string | null;
      goalIds: string[];
      labelIds: string[];
      parent?: { color: string | null } | null;
    }) =>
      resolveTaskColor(
        [
          { color: task.color, from: 'this task' },
          /*
            The parent, right behind the task's own choice.

            `resolveColor` asks each source at read time and never writes back, so putting the parent
            here gives subtasks live inheritance with no migration and nothing to go stale. It sits
            below an explicit colour on the subtask — that is a deliberate choice about this piece of
            work — and above the label, project and goal, which a subtask inherits from its parent
            anyway, so it only ever changes the answer when the parent was coloured by hand.
          */
          { color: task.parent?.color ?? null, from: 'its parent' },
          fromLabel(task.labelIds) ?? { color: null, from: '' },
          nearestColored(
            task.projectId ? (byId.get(task.projectId) ?? null) : null,
            byId,
            (node) => names.get(node.id) ?? 'its project',
          ) ?? { color: null, from: '' },
          {
            color: task.goalIds[0] ? (goalRaw.get(task.goalIds[0]) ?? null) : null,
            from: 'its goal',
          },
          // The goal's *assigned* class is the last word — see `resolveTaskColor`.
        ],
        task.goalIds[0] ? (goalClassById.get(task.goalIds[0]) ?? null) : null,
      );
  }, [projectsForColor.data, goalTitles.data, labelsForColor.data, goalClassById]);

  /*
    Which habits are actually asking for something on the day being shown.

    A daily habit always is. A weekly or monthly one is only if it is still short of where it needs
    to be — `onTrack` being false is exactly that statement. One already met for the period is not
    work left that day, and counting it made the evening check-in read as nearly finished.

    "The day being shown", not "today": the plan is fetched for `date` and — since the habits in it
    are read at that date rather than at the wall clock — `onTrack` is a statement about the period
    `date` falls in. Paging back to a kept week now empties this count instead of reporting the
    current week's shortfall under an August date.

    What is in it, decided (`01a15a31-35de`):

    - **A stale reading is.** A daily reading is due because it is daily, and a weekly one unread
      this week is short by the service's own `onTrack`. Taking the reading is the work, and the row
      says so (*not read today*); it is not done until somebody does, since its verdict is
      `too_early`, which `verdictIsMet` does not keep.
    - **A paused habit is not.** It is not asked — `undoneRank` sorts it below the done ones for the
      same reason. The day plan already leaves out a habit not running on the day (`isRunningOn`
      in core's `getHabitsStatus`), so this is true twice; the filter says it here, where the count
      is, rather than leaving the rule to a fact about another package.
  */
  const dueOnDay = useMemo(
    () =>
      (plan.data?.habits ?? []).filter(
        (h) => h.verdict !== 'paused' && (h.period === 'day' || !h.onTrack),
      ),
    [plan.data],
  );

  /*
    The pane's order: due and undone, then due and done, then the not-due behind *show N more*.

    It was the service's order, which is alphabetical — the health nut's thirty-one rows came out
    4,558px tall at 375 with the nine not due today mixed among the twenty-two that were, and the
    first habit actually behind 958px down (`01a08096-e4e0`). The rank is the Habits screen's own
    `undoneRank`, and so is the remembered order — one choice made on either screen applies to
    both. Only the default differs: this is the screen habits are logged on, so undone first unless
    somebody has chosen otherwise.

    Read once, on arrival, because the control that changes it is on the Habits screen.
  */
  const [habitOrder] = useState(() => readOrder('undone'));
  const [showNotDue, setShowNotDue] = useState(false);
  /*
    And the hold, which is not optional: logging an undone row drops it a tier, and a weekly habit
    logged to target leaves the due set altogether. Without this the row leaves from under the thumb
    and the next one's control slides into the pixels the tap just left — the defect `useLogHold`
    records arriving twice on Habits. Paging the day or opening the not-due rows releases it.
  */
  const habitHold = useLogHold(`${date}|${showNotDue}`, date);
  /*
    The rows the header counts and the pane draws as due — the same set, so the two cannot disagree.

    `dueOnDay` until the first log of the day shown; from then, what it was at that moment, plus a
    row you logged that became due by it (an undo taking a weekly habit back under its target). A
    log on one row judges others: an entry anywhere is the account's presence, which settles a
    tallied ceiling's week, so *Added sugar* went met and left the count and the list, untouched,
    when Deep sleep was logged — *0/25* became *1/24* (`01a15a31-35de`). Held by the same hold as
    the order; see `counted` in `useLogHold`. It holds the set, not the verdicts — so the same log
    can still raise the numerator below, which is why `doneOnDay` says it should.
  */
  const countedOnDay = (() => {
    const all = plan.data?.habits ?? [];
    const frozen = habitHold.counted;
    if (!frozen) return dueOnDay;
    const dueNow = new Set(dueOnDay.map((h) => h.id));
    return all.filter((h) => frozen.has(h.id) || (habitHold.held.has(h.id) && dueNow.has(h.id)));
  })();
  /*
    Done is what the row's own words call kept — `verdictIsMet`, the predicate `verdictWord` is
    checked against — not `loggedOnDay > 0`, which counted Deep sleep at 84 of 90 minutes while
    its row read *on pace*, and could never count a ceiling kept clean.

    So the numerator can rise on rows you did not touch, and that is the rule rather than a leak
    (`01a15ee1-b10c`). A tallied ceiling is judged only on a period the account showed use in
    (Phase 84), and the first entry of the day on *any* habit is that use: log Deep sleep at 84 of
    90 and every silent daily `at most` goes from unjudged to *clean* or *at target* — its own row
    says so — along with any weekly one whose week this is. The hold above freezes which rows are
    counted, not their verdicts; freezing those too would print *0 kept* over rows reading *clean*,
    the two-predicates-for-one-noun defect this count was rebuilt to remove. If the rows are wrong
    to say *clean* at 09:00, that is an argument about the evidence rule, and the count follows
    whatever word replaces it. `dueTodayCount.e2e` walks that first log.
  */
  const doneOnDay = countedOnDay.filter((h) => verdictIsMet(h.verdict)).length;
  const habitPane = (() => {
    const all = plan.data?.habits ?? [];
    const due = new Set(countedOnDay.map((h) => h.id));
    // A held row stays among the rows it was drawn with, whatever logging it did to `due`.
    const shown = all.filter((h) => due.has(h.id) || habitHold.held.has(h.id));
    const notDue = all.filter((h) => !due.has(h.id) && !habitHold.held.has(h.id));
    /*
      With nothing due there is nothing to put first, and collapsing every row would leave the pane
      a heading over a button — so an account whose habits are all met for the period sees them all.
    */
    const collapsed = shown.length > 0 && !showNotDue;
    const ranked = [
      ...(habitOrder === 'undone' ? byUndone(shown) : shown),
      ...(collapsed ? [] : notDue),
    ];
    return {
      rows: habitHold.arrange(ranked, (h) => h.id),
      // Zero when there is nothing behind the control — including the all-met case above.
      notDueCount: shown.length > 0 ? notDue.length : 0,
      collapsed,
    };
  })();
  const holdHabit = (id: string) =>
    habitHold.hold(
      id,
      habitPane.rows.map((h) => h.id),
      countedOnDay.map((h) => h.id),
    );

  const blockGoalClass = useMemo(() => {
    const out = new Map<string, string>();
    for (const task of [...(plan.data?.tasks ?? []), ...(plan.data?.candidates ?? [])]) {
      const first = task.goalIds[0];
      const cls = first ? goalClassById.get(first) : undefined;
      if (cls) out.set(task.id, cls);
    }
    return out;
  }, [plan.data, goalClassById]);
  const titleById = useMemo(() => {
    const map = new Map<string, string>();
    const walk = (nodes: { goal: { id: string; title: string }; children: unknown[] }[]) => {
      for (const node of nodes) {
        map.set(node.goal.id, node.goal.title);
        walk(node.children as typeof nodes);
      }
    };
    walk((goalTitles.data ?? []) as never);
    return map;
  }, [goalTitles.data]);

  /*
    Title *and* colour, so the shortlist draws the same chip the task list does.

    This screen had only the titles, so its chip came out with no colour class — dashed, grey and
    indistinguishable from a label, while the identical goal on Tasks was a solid amber capsule.
    The colour map has been here since Phase 59 for the row's own tint; the chip simply never
    asked it.
  */
  const goalChipById = useMemo(() => {
    const map = new Map<string, { title: string; colorClass: string }>();
    for (const [id, title] of titleById) {
      map.set(id, { title, colorClass: goalClassById.get(id) ?? '' });
    }
    return map;
  }, [titleById, goalClassById]);

  const invalidate = () => queryClient.invalidateQueries();

  /*
    Escape abandons a selection.

    The rule the whole gesture rests on: nothing has been written yet, so leaving must cost nothing
    and must be obvious. Bound on the document rather than the panel because the caret is inside
    capture's input by then, and that input is not where the selection lives.
  */
  useEffect(() => {
    if (!drawing && !pending) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setDrawing(null);
      setPending(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawing, pending]);

  /*
    The block for a task that was just drawn.

    Capture makes the task — the same capture as everywhere else, so it arrives with whatever goal,
    labels and priority were set on the facet row — and this hangs the drawn time off it. Two calls
    rather than one endpoint, because a task created on the calendar should be a task first and a
    block second: if the block fails the task still exists, which is the right way round to fail.
  */
  const placeDrawn = useMutation({
    mutationFn: (args: { taskId: string; startsAt: string; durationMinutes: number }) =>
      scheduleTask(args.taskId, args.startsAt, args.durationMinutes),
    onSuccess: (result) => {
      setConflicts(result.conflicts);
      setPending(null);
      void invalidate();
    },
  });

  const place = useMutation({
    mutationFn: (args: { taskId: string; startsAt: string }) =>
      scheduleTask(args.taskId, args.startsAt),
    onSuccess: (result) => {
      setConflicts(result.conflicts);
      void invalidate();
    },
  });

  const move = useMutation({
    mutationFn: (args: { eventId: string; startsAt: string }) =>
      moveBlock(args.eventId, { startsAt: args.startsAt }),
    onSuccess: (result) => {
      setConflicts(result.conflicts);
      void invalidate();
    },
  });

  const resize = useMutation({
    mutationFn: (args: { eventId: string; durationMinutes: number; updateEstimate: boolean }) =>
      moveBlock(args.eventId, {
        durationMinutes: args.durationMinutes,
        updateTaskEstimate: args.updateEstimate,
      }),
    onSuccess: (result) => {
      setConflicts(result.conflicts);
      void invalidate();
    },
  });

  /**
   * Slots long enough for the task being fitted.
   *
   * Keyed on the duration as well as the task, so two tasks of the same length share a
   * cached answer and a re-fit after a block moves asks again. The window is the user's own
   * day, not the endpoint's 9-to-18 default, or a 06:00 gym slot would never be offered.
   */
  const slots = useQuery({
    queryKey: [
      'free-slots',
      date,
      fitting?.durationMinutes,
      fitting?.templateId ?? null,
      plan.data?.events.length,
    ],
    queryFn: () =>
      getFreeSlots(
        date,
        fitting!.durationMinutes,
        {
          fromHour: dayWindow?.startHour ?? START_HOUR,
          toHour: dayWindow?.endHour ?? END_HOUR,
        },
        fitting!.templateId ?? null,
      ),
    enabled: fitting !== null,
  });

  const remove = useMutation({ mutationFn: deleteBlock, onSuccess: invalidate });
  const finish = useMutation({ mutationFn: completeTask, onSuccess: invalidate });

  /**
   * Work you can drag onto the day.
   *
   * The server decides what qualifies — unscheduled, overdue, or already dated today —
   * and orders it. Previously this filtered `plan.tasks`, which only ever contained
   * tasks already carrying this date, so nothing new could reach the list without
   * opening the task first and dating it by hand.
   */
  const shortlist = useMemo(() => plan.data?.candidates ?? ([] as Task[]), [plan.data]);

  /*
    Filtering the shortlist, by title or by label name.

    The same rule the Tasks screen's search follows, so `work` finds work labelled `work` on both.
    Matched here rather than through the API because the rows are already in the browser — and
    because the shortlist is a fixed candidate set, not a query.
  */
  const [shortlistQuery, setShortlistQuery] = useState('');
  const visibleShortlist = useMemo(() => {
    const q = shortlistQuery.trim().toLowerCase();
    if (q === '') return shortlist;
    const labelNames = new Map(
      (labelsForColor.data ?? []).map((l) => [l.id, l.name.toLowerCase()] as const),
    );
    return shortlist.filter(
      (task) =>
        task.title.toLowerCase().includes(q) ||
        task.labelIds.some((id) => labelNames.get(id)?.includes(q)),
    );
  }, [shortlist, shortlistQuery, labelsForColor.data]);
  const hiddenByFilter = shortlist.length - visibleShortlist.length;

  /*
    The same rows in three piles — Started, Due or planned, Captured — each in the server's order.

    See `shortlistGroups.ts` for the rule and for why nothing is re-sorted. Grouped twice: the whole
    list decides where the fold falls, the filtered one decides what is drawn, for the reason the
    fold's own comment gives about the filter box.
  */
  const groups = useMemo(() => groupShortlist(shortlist), [shortlist]);
  const visibleGroups = useMemo(() => groupShortlist(visibleShortlist), [visibleShortlist]);
  /*
    The Board's in-progress rows that are not under *Started*, in words — see
    `inProgressElsewhere`. Against the whole Started group rather than the filtered one: a row the
    search box hides is still on this list, and the filter's own line already says how many it hid.
  */
  const inProgressRows = useInProgressRows();
  const startedElsewhere = useMemo(
    () =>
      inProgressRows === null
        ? null
        : elsewhereText(inProgressElsewhere(inProgressRows, groups.started)),
    [inProgressRows, groups.started],
  );
  /*
    How much of the captured pile is open: none, the first batch, or all of it.

    Local and forgotten on reload, deliberately. The fold is the default because thirty-four
    untouched captures are what buried the eleven started rows; remembering "show all" would bring
    that screen back on every visit after the first look.
  */
  const [capturedShown, setCapturedShown] = useState<'none' | 'batch' | 'all'>('none');

  /*
    The last status command pressed on this screen — a Start or a tick — with the way back.

    A Start moves the row — into the Started group, on the refetch — and the screen used to say
    nothing about it (`01a15a31-174b`). Holding the row in its old group was considered and
    refused (the architect's decision on that task): it would put an `in_progress` row under *Due
    or planned* wearing a *started* mark, one row with two predicates. So the row goes where its
    status puts it, and this is what follows it there — the readback at the top of the pane naming
    it with the way back, and `data-just-started` on the row in its new place.

    A tick removes the row altogether, and until `01a03813-a118` it did so in silence: the
    minimalist ticked *call the bank tomorrow*, the row went, and getting it back meant knowing to
    go to Tasks → Filter → Show done. The box now records into the same line, so it says
    *Completed “X”.* with *Reopen*, in the words Tasks uses for the same act. One line for both
    presses, because they are one slot: the next press, of either kind, replaces it.

    Its lifetime is the readback's, not a timer: dismiss, the way back, the next press, or leaving
    the screen (it is component state) all clear it. Dragging to the timeline, *Find a slot* and
    the overflow's defer record nothing.

    A refusal turns it into the line saying so, and the row loses its *just started* mark
    (`01a15e3c-61e3`) — `withdraw`, matched on the press's claim.
  */
  const readback = useCommandReadback();
  const justStarted =
    readback.line?.landed === 'in_progress' && !readback.line.failed
      ? readback.line.from[0]?.id
      : undefined;

  /**
   * Where what fits ends.
   *
   * A running sum down the shortlist in the order the server ranked it, against the day's free
   * minutes. The first row that would take the total past them is the fold, and everything after
   * it is below — a fold is one line, so a short task further down does not climb back over it.
   *
   * §4 is *warn, never block*: below the fold a row is dimmed and labelled and stays exactly as
   * draggable, exactly as present, as it was. Nothing here removes a row from the list.
   *
   * Computed over the whole shortlist rather than the filtered view, so typing in the filter box
   * cannot move the line — "what fits today" is a fact about the day, not about the search.
   * Each row counts what is still *homeless*, matching both the pill on the row itself
   * ("30m left of 60m") and the server's `shortlistedMinutes` — which sums over `candidates`,
   * the very array this loop walks. That is what keeps the bar and the line from disagreeing: if
   * the number were summed over a wider set, the bar could go amber over minutes belonging to
   * rows that are not in this list, and there would be no fold to draw and nothing for the footer
   * to offer to move. A row nobody estimated adds
   * nothing and so never pushes the fold; that is what the `unestimated` count beside the bar is
   * for, and why it is said out loud rather than assumed to fit.
   *
   * **Walked in the order the list is drawn**: Started, then Due or planned. Grouping moved started
   * work to the top, and a fold summed in the server's order would draw its line under one row
   * while counting minutes from rows beneath it — a started two-hour task the server ranks below
   * two planned hours would sit at the top of the list *dimmed*, with the line further down.
   * Started first is also the claim the grouping makes: finishing comes before starting.
   *
   * The captured group is outside it. Its rows carry no estimate by definition, so they could only
   * ever be "below" by position — and the cut that bounds them is the group's own collapse, which
   * is the cut this line, honest about minutes, cannot make.
   */
  /*
    Plainly, not memoised: `plannedByTask` above is a fresh Map on every render, so a `useMemo`
    keyed on it would recompute exactly as often while reading as though it did not. It is one
    pass over at most fifty rows.
  */
  const fold = ((): { below: Set<string>; firstBelowId: string | null } => {
    const budget = plan.data?.budget;
    const below = new Set<string>();
    let firstBelowId: string | null = null;
    if (!budget) return { below, firstBelowId };
    let running = 0;
    for (const task of [...groups.started, ...groups.dueOrPlanned]) {
      const homeless = Math.max(0, (task.estimateMinutes ?? 0) - (plannedByTask.get(task.id) ?? 0));
      if (firstBelowId !== null || running + homeless > budget.freeMinutes) {
        if (firstBelowId === null) firstBelowId = task.id;
        below.add(task.id);
        continue;
      }
      running += homeless;
    }
    return { below, firstBelowId };
  })();

  /*
    The one action the warning offers: move what does not fit to tomorrow.

    `bulkUpdateTasks`, the same single write `BulkEdit`'s "Tomorrow" button makes — one request and
    one audit row, rather than a loop of updates that can half-succeed. And tomorrow relative to
    the day being *viewed*, not to the browser's clock: this pane answers for the day on screen.
  */
  const deferOverflow = useMutation({
    mutationFn: (ids: string[]) => bulkUpdateTasks(ids, { scheduledDate: shiftDay(date, 1) }),
    onSuccess: invalidate,
  });

  const dayStart = plan.data?.dayStart;

  /** Converts a drop position into a snapped instant on the day. */
  const instantAt = (offsetY: number): string | null => {
    if (!dayStart) return null;
    const rawMinutes = window.startHour * 60 + (offsetY / PX_PER_HOUR) * 60;
    const snapped = Math.round(rawMinutes / SNAP_MINUTES) * SNAP_MINUTES;
    return new Date(new Date(dayStart).getTime() + snapped * 60_000).toISOString();
  };

  /** Snapped start for a pointer position, in minutes from midnight. */
  const minutesAt = (offsetY: number): number => {
    const raw = window.startHour * 60 + (offsetY / PX_PER_HOUR) * 60;
    const snapped = Math.round(raw / SNAP_MINUTES) * SNAP_MINUTES;
    return Math.min(Math.max(snapped, window.startHour * 60), window.endHour * 60);
  };

  /**
   * The handful actually drawn.
   *
   * The endpoint returns every valid start at half-hour steps, which for an empty day and a
   * one-hour task is about twenty overlapping bands — a wall, not a set of choices.
   */
  const choices = useMemo(
    () => (fitting ? distinctSlots(slots.data ?? [], fitting.durationMinutes) : []),
    [slots.data, fitting],
  );

  /** Take one of the offered slots — placing a task, or moving a block that already exists. */
  const takeSlot = (slot: FreeSlot) => {
    if (!fitting) return;
    if (fitting.eventId) move.mutate({ eventId: fitting.eventId, startsAt: slot.start });
    else if (fitting.taskId) place.mutate({ taskId: fitting.taskId, startsAt: slot.start });
    setFitting(null);
    setSelectedBlock(null);
  };

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const bounds = e.currentTarget.getBoundingClientRect();
    setPreviewMinutes(minutesAt(e.clientY - bounds.top));
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setPreviewMinutes(null);
    const bounds = e.currentTarget.getBoundingClientRect();
    const startsAt = instantAt(e.clientY - bounds.top);
    if (!startsAt) return;

    // Both the task id and the event id travel in dataTransfer rather than in React
    // state. State set during dragstart is only visible to the drop handler if a
    // re-render happened in between, which is not guaranteed — dataTransfer is what
    // the drag API provides for exactly this.
    const movingEventId = e.dataTransfer.getData('planner/event');
    const draggedTaskId = e.dataTransfer.getData('planner/task');

    if (movingEventId) move.mutate({ eventId: movingEventId, startsAt });
    else if (draggedTaskId) place.mutate({ taskId: draggedTaskId, startsAt });

    setDragging(null);
  };

  /**
   * The hours to draw. The preference decides the usual shape of a day; anything
   * scheduled outside it widens the view, so a block is never off-screen.
   */
  const window = useMemo(() => {
    const preference = dayWindow ?? { startHour: START_HOUR, endHour: END_HOUR };
    if (!plan.data) return preference;
    return visibleWindow(
      preference,
      plan.data.events.map((e) => ({
        startMinutes: minutesFromDayStart(e.startsAt, plan.data!.dayStart),
        endMinutes: minutesFromDayStart(e.endsAt, plan.data!.dayStart),
      })),
    );
  }, [plan.data, dayWindow]);

  const hours = Array.from(
    { length: window.endHour - window.startHour },
    (_, i) => window.startHour + i,
  );

  /*
    The clock, ticking once a minute.

    A minute is the resolution of the label and of a 56px hour — a second-by-second interval would
    re-render the whole timeline sixty times for the same pixel. Seeded from `Date.now()` on mount
    so the line is right immediately rather than up to a minute late.
  */
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  /*
    `[`, `]` and `t` step the day.

    The house typing guard — a shortcut that fires while you are writing a task title is worse
    than no shortcut. This screen carried its own inline copy of that test, which is the drift
    `isTypingTarget` exists to stop.

    Registered on this screen only, so the keys are free elsewhere — and the same call publishes
    the `today` group to the `?` panel, so the two can never disagree about which screen you are
    on. See `keyScope.ts`.
  */
  useKeys('today', (e: KeyboardEvent) => {
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    if (e.key !== '[' && e.key !== ']' && e.key !== 't') return;
    if (isTypingTarget()) return;
    e.preventDefault();
    if (e.key === 't') setDate(todayIso(timezone));
    else setDate((d) => shiftDay(d, e.key === '[' ? -1 : 1));
  });

  /*
    Where "now" is, or null when the line would lie — a day that is not today, or a time outside
    the hours drawn. See `nowOffset`.
  */
  const marker = plan.data
    ? nowOffset({ now, dayStart: new Date(plan.data.dayStart).getTime(), window })
    : null;

  /** Column assignment, so two things at ten o'clock are both visible. */
  const placements = useMemo(() => {
    if (!plan.data) return new Map<string, { column: number; columns: number }>();
    return layoutOverlaps(
      plan.data.events.map((e) => ({
        id: e.id,
        startMinutes: minutesFromDayStart(e.startsAt, plan.data.dayStart),
        endMinutes: minutesFromDayStart(e.endsAt, plan.data.dayStart),
      })),
    );
  }, [plan.data]);

  /*
    One shortlist row, wherever it is drawn — the same row in all three groups, so a capture opened
    from the fold is as draggable, startable and completable as anything above it.

    It draws no blocked state, because it is never handed a blocked task: the shortlist is
    `pickCandidates`' output, which drops blocked work unless asked for it, and `getDayPlan` never
    asks. `dayCandidates.spec.ts:59` ('does not offer blocked or finished work') is the guard that
    holds that. The row used to carry five `isBlocked` branches anyway — not draggable, dimmed, a
    *Blocked by an open task* title, no *Find a slot*, a second blocked badge — none of which could
    fire, and one of which was read as a reachable double mark and cost a phase slot to disprove
    (`01a15e10-2697`). If the day plan ever starts offering blocked work, that decision is recorded
    beside `pickCandidates` in `getDayPlan`, and this row then needs the state back by design.
    `TaskProgressMark` below keeps its own blocked arm: that one is Tasks', where it is reachable.
  */
  const shortlistRow = (task: Task) => (
    <Fragment key={task.id}>
      {/*
                    The line where what fits ends.

                    A separator, not a boundary: the rows under it are still here, still draggable
                    and still first-class — the day simply has no room left for them at their own
                    estimates. Rendered as a list item so it sits in the flow of the list rather
                    than floating over it, and `aria-hidden` because the rows below carry the same
                    fact in words on each one.
                  */}
      {fold.firstBelowId === task.id && (
        <li className="shortlist-fold" data-testid="shortlist-fold" aria-hidden="true">
          <span>What fits ends here</span>
        </li>
      )}
      <li
        data-testid="shortlist-task"
        data-below-fold={fold.below.has(task.id) ? 'true' : undefined}
        data-just-started={justStarted === task.id || undefined}
        data-task-title={task.title}
        onContextMenu={(e) => menu.open(e, task)}
        draggable
        onDragStart={(e) => {
          e.dataTransfer.setData('planner/task', task.id);
          setDragging({
            taskId: task.id,
            title: task.title,
            // The server applies the same fallback chain when it has no
            // estimate (§5.6 rule 3); 30 minutes is the last step of it.
            durationMinutes: task.estimateMinutes ?? 30,
          });
        }}
        onDragEnd={() => {
          setDragging(null);
          setPreviewMinutes(null);
        }}
        className={`${rowColor(task).colorClass ?? ''} ${
          dragging?.taskId === task.id ? 'dragging' : ''
        } ${fold.below.has(task.id) ? 'below-fold' : ''}`}
        title={
          rowColor(task).colorClass
            ? `Drag onto the timeline · colour from ${rowColor(task).from}`
            : 'Drag onto the timeline'
        }
      >
        <span className="grip" aria-hidden="true">
          ⠿
        </span>
        {/* Compact: no move menu on a drag source, but completing and starting are
                      each one press on the screen where you decide what to work on. */}
        {/* Recorded, so a tick names the task it removed and offers Reopen — see `readback`. */}
        <TaskStatusControl
          task={task}
          onAct={(status) => readback.record(status, [task])}
          onFail={readback.withdraw}
        />
        {/*
                    Title and meta stacked, not strung out in one flex row.

                    The shortlist column is 13rem and the row carried six things across it — grip,
                    box, title, the slot button, a chip or two, a goal. The title got what was left,
                    which was about 60px: "Book the ferry to Gotland and confirm the cabin" rendered
                    as nine lines of one word each, and the goal chip beside it was clipped to "S".
                    Giving the title the full width and letting the meta wrap under it costs one
                    line on the rows that have chips and none on the rows that do not.
                  */}
        <div className="shortlist-main">
          <button className="link shortlist-title" onClick={() => onOpenTask(task.id)}>
            {task.title}
          </button>
          {/* Truncates before the title does — the day shortlist is the narrowest of
                        the full-width surfaces. */}
          {task.parent && <ParentLink parent={task.parent} onOpen={onOpenTask} />}
          {/*
                        Where the task had got to, in `shortlist-main` rather than the meta strip
                        below — the one surface this finding measured most directly. Every row's
                        text was dumped in that persona round and not one contained a fraction, so
                        the screen you pick the day's work from could not say that a task was
                        part-way in or what the next step on it was. See `ChecklistLine`.
                      */}
          <ChecklistLine task={task} />
          <span className="shortlist-meta">
            <SubtaskPill task={task} />
            {/*
                        The state, on the surface where you choose what to do next — and, before
                        anything has started, the offer to reach it.

                        The shortlist showed a colour band, a goal chip and an estimate and nothing
                        else, so the thing you pick work from could not tell you that a task was
                        already in progress, that it was the high-priority one, or what kind of
                        work it is. All three were on the task list next door.

                        It stays in this strip, unlike the task list's, and the measurement is why
                        rather than the symmetry. At 375 a shortlist row is 37px and gains a second
                        line the moment this slot holds a 24px control — 61px — and it gains it
                        *wherever* the control is put: 61 beside the status box, 65 on the title's
                        own line, because `.shortlist-title` claims a 12rem basis of a 13rem column
                        and a sibling wraps. There is no free placement on this row, so the slot
                        stays where the state has always been shown. The cost is bounded: this is
                        the day's chosen handful, not the 56-row list, and a row carrying chips has
                        wrapped to two lines since the shortlist was laid out.
                      */}
            <TaskProgressMark
              task={task}
              onAct={() => readback.record('in_progress', [task])}
              /* Only while the readback is still the one this press put up: a later press has
                 replaced it, and that one's claim is not this failure's to withdraw. Matched on the
                 press, not the row — a double press of the same Start used to have its landed
                 second press reported as refused when the first one failed (`01a16009-e4e1`). */
              onFail={readback.withdraw}
            />
            <PriorityMark priority={task.priority} />
            {/* The alternative to aiming. Dragging is still there for people who
                      already know where the task goes. */}
            <button
              className="link fit-button"
              title="Show me where this fits today"
              aria-label={`Find a slot for ${task.title}`}
              onClick={() =>
                setFitting(
                  fitting?.taskId === task.id
                    ? null
                    : {
                        taskId: task.id,
                        title: task.title,
                        durationMinutes: task.estimateMinutes ?? 30,
                        templateId: task.templateId,
                      },
                )
              }
            >
              {fitting?.taskId === task.id ? '×' : '◷'}
            </button>
            {/*
                        Red was reserved for lateness that does not exist here. This said "overdue"
                        for any task whose `scheduledDate` had passed, while `dueAt` was null — the
                        tooltip even admitted "Was planned for …". Nothing was late; the only
                        offence was having once been planned, reported as failure on the first
                        screen of the day. `dueLook` states the plan neutrally and keeps red for a
                        real deadline that has gone by.

                        `date`, not today: this pane answers the question for the day being viewed.
                      */}
            {(() => {
              /* The viewed day, judged in the account's zone: `dueAt` is an instant, and
                           which day it lands on is a question about a zone. */
              const look = dueLook(task, date, timezone);
              return look ? (
                <span className={dueClass(look)} title={dueTitle(look)}>
                  {look.text}
                </span>
              ) : null;
            })()}
            {task.scheduledDate === date && <span className="pill">today</span>}
            {/*
                        How much of it still has nowhere to happen.

                        A task with two hours of work and one hour blocked out stays in the
                        shortlist so the rest can be planned — and then has to say why it is still
                        here, or it reads as a task somebody forgot to drag.
                      */}
            {task.estimateMinutes &&
              (plannedByTask.get(task.id) ? (
                <span className="pill remaining" data-testid="shortlist-remaining">
                  {task.estimateMinutes - (plannedByTask.get(task.id) ?? 0)}m left of{' '}
                  {task.estimateMinutes}m
                </span>
              ) : (
                <span className="pill">{task.estimateMinutes}m</span>
              ))}
            {/* Which larger thing this serves. Only the first: a row is not the place to
                      list three, and the task detail has the full set. */}
            {goalChipsFor(task, goalChipById)
              .slice(0, 1)
              .map((chip) => (
                <span
                  key={chip.title}
                  className={`pill goal-chip ${chip.colorClass}`}
                  title={`Goal: ${chip.title}`}
                >
                  {chip.title}
                </span>
              ))}
            {task.labelIds
              .map((id) => labelNameById.get(id))
              .filter((name): name is string => name !== undefined)
              .slice(0, 1)
              .map((name) => (
                <span key={name} className="pill label-chip" title={`Label: ${name}`}>
                  {name}
                </span>
              ))}
            {task.labelIds.length > 1 && (
              <span className="pill label-chip" title="Open the task to see them all">
                +{task.labelIds.length - 1}
              </span>
            )}
          </span>
        </div>
      </li>
    </Fragment>
  );

  return (
    <section>
      {/*
        Two arrows and the date between them.

        This row was five controls: Previous, Today, Next, a date input, and Plan my day. Today
        duplicated the nav tab directly above it; the arrows say what the words did; and Plan my
        day is an action on the shortlist rather than on the date, so it now lives at the head of
        the shortlist pane where the work it plans actually is.
      */}
      <div className="capture today-controls">
        <PeriodNav
          label="day"
          onPrevious={() => setDate(shiftDay(date, -1))}
          onNext={() => setDate(shiftDay(date, 1))}
        >
          <input
            type="date"
            className="day-date"
            aria-label="Show a date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </PeriodNav>
        {/*
          The way to this day's page, beside the date it is a page for.

          Outside `PeriodNav`'s slot, which holds one thing and already holds the picker. Its own
          control rather than making the date itself a link: the date input is how you *change* the
          day, and a field that both edits and navigates is a field where one of the two happens by
          accident.
        */}
        <button type="button" className="ghost" onClick={() => onOpenPage(date)}>
          Page
        </button>
      </div>

      <ConflictNotice conflicts={conflicts} onDismiss={() => setConflicts([])} />

      {/*
        Before the panes: what the day owes from earlier — the late pile counted once, and the plans
        you made and did not keep, asked about once.

        One band, where there were two. The carry-over band and a late band under it counted the
        same plan-only stale tasks twice, so the screen opened on two figures about one pile
        (`01a15a31-14a5`); `CarriedOver` now says the late total split by which clock made it late,
        from the server's own halves, and keeps its verbs for the plan half.

        Only on the real today. Looking back at a Tuesday in July to see what happened is not the
        moment to be offered "move all of this to the 14th", and "6 late" is a statement about now
        — reading it under another day's heading would be a figure from one day printed on another.
      */}
      {date === todayIso(timezone) && <CarriedOver date={date} onSeeLate={onSeeLate} />}

      {/*
        And what somebody else is holding, which is the other half of why today looks empty.

        The deliverable is out of the shortlist — `pickCandidates` drops a waiting task, so the
        planner will not put it on the calendar — but the *chase* is yours and is today's work.
        This rail is the only place that difference is visible; without it a wait is indistinguish-
        able from work that simply vanished.

        Gated on the real today for the same reason `CarriedOver` is. A wait is current state, and
        offering "Chased" while looking back at a Tuesday in July would record today's chase from
        a screen about a day three weeks gone.
      */}
      {date === todayIso(timezone) && <WaitingRail onOpenTask={onOpenTask} />}

      {/*
        And, above the day rather than inside it: where the work is, for somebody who has none.

        Beside `CarriedOver` because it is the same kind of thing — a band that answers the day
        when the day is not answering for itself — and because the shortlist column it started in
        is a narrow rail on a wide screen and a tab on a narrow one, which is the wrong place for
        the one control that exists to be *found*. It renders nothing unless the server says there
        is somewhere to send you, so this is invisible on every account that has work of its own.

        Gated on the shortlist as well, so an account that has planned its day is not offered a
        way out of it: the pointer belongs to the screen that is empty.
      */}
      {plan.data && shortlist.length === 0 && <WorkElsewhere onOpenBoard={onOpenProjectBoard} />}

      <QueryState query={plan} />

      {/*
        Only rendered on narrow screens (CSS), and it switches rather than filters: all three
        panes stay mounted, so the timeline keeps its drag state and scroll position when you
        step away to tick a habit off.
      */}
      {plan.data && (
        <div className="pane-switch" role="tablist" aria-label="Today panes">
          {/*
            No habits, no Habits tab.

            The pane rendered "No habits yet." to an account that tracks none — which is a third of
            the day screen spent saying the app has a feature you are not using. A control that can
            do nothing is not shown; the tab comes back the moment a habit exists.
          */}
          {(['shortlist', 'timeline', 'habits'] as const)
            .filter((id) => id !== 'habits' || plan.data!.habits.length > 0)
            .map((id) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={pane === id}
                className={pane === id ? 'is-selected' : ''}
                onClick={() => {
                  // Your choice wins from here on; the derivation above stops having an opinion.
                  setPaneChosen(true);
                  setPane(id);
                }}
              >
                {id === 'shortlist' ? 'Shortlist' : id === 'timeline' ? 'Timeline' : 'Habits'}
              </button>
            ))}
        </div>
      )}

      {/*
        `data-empty` names the panes holding nothing, so the grid can stop paying for them.

        The 1 : 2 : 1 proportion is deliberate and was asked for, and it is right whenever the day
        has a shape. What it does not account for is a pane with nothing in it: an account with
        tasks and no blocks got a 302px shortlist beside a 604px empty timeline — twice the width
        for the pane holding nothing — and the same layout crushed 31 habits into 302px beside that
        same empty timeline.
      */}
      {plan.data && (
        <div
          className="today-panes"
          data-pane={pane}
          data-empty={[
            plan.data.candidates.length === 0 && plan.data.tasks.length === 0 ? 'shortlist' : '',
            plan.data.events.length === 0 ? 'timeline' : '',
            plan.data.habits.length === 0 ? 'habits' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <aside className="pane" data-pane-id="shortlist">
            {/*
              "Plan my day" is gone, and so is `ProposalPanel`.

              It took more space than it earned: it ranked purely on `dueAt`, so nine-day-old work
              took the morning while today's escalation was skipped for want of a slot, and its
              result panel listed forty skipped tasks with thirty repetitions of one sentence. The
              endpoint, the planner domain and their tests are untouched, so bringing it back once
              it ranks by urgency is a button rather than a rebuild. The panel component is
              deleted rather than left unrendered — git holds it, and dead UI rots quietly.
            */}
            <div className="pane-head">
              <h3>Shortlist</h3>
            </div>

            {/*
              The Board's sentence, on top of the list it is about.

              The ADHD persona found it on the Board — "the only screen that understood my
              situation" — two clicks from the screen where the eleven started rows were. It is the
              Board's own component reading the Board's own query, not a count of this shortlist:
              see `WipNotice.tsx` for why those would disagree.
            */}
            <TodayWip />

            {/*
              What the last Start or tick did, above the heading that counts the pile it joined —
              pinned under the header, like Tasks' and the Board's, so a tick made far down the
              shortlist is answered where the reader is (see `ReadbackSlot`).

              No "— N in progress" here, unlike Tasks: the *Started* heading below carries the
              figure at every size and ticks on the same refetch. The Board's sentence above cannot
              stand in for it — it counts every `in_progress` row and is silent at three or fewer,
              which is exactly when a Start most often happens. The readback names the task, which
              neither figure can.
            */}
            <div className="command-dock">
              <ReadbackSlot readback={readback} />
            </div>

            {/*
              What the day has room for, under the head where the count used to be the only fact.

              Shown whenever there is a shortlist at all, not only when it is over — a bar that
              appears only to scold has no baseline to be read against, and "you have four hours
              and have asked for two" is the reassurance half of the same sentence. Whether it
              earns its place both ways is the first thing to measure; the amber state is the
              cheap thing to hide later if it turns out to be nagging.
            */}
            {plan.data && shortlist.length > 0 && (
              <DayBudgetBar budget={plan.data.budget} overflow={fold.below.size} />
            )}
            {/*
              Only where the timeline is on screen to drag onto.

              At 375 the panes are tabs and the timeline is a different one, so this printed an
              instruction naming something not on the page — a DOM probe returned a `.timeline`
              rect of 0×0 while the hint was visible. On the first screen of the app.
            */}
            {!panesStack && (
              <p className="muted small">Drag onto the timeline to plan it for this day.</p>
            )}

            <TemplateQuickAdd />

            {fitting && (
              <p className="subtext fitting-notice" data-testid="fitting-notice">
                {slots.isPending
                  ? 'Looking for a slot…'
                  : choices.length === 0
                    ? `No free ${fitting.durationMinutes}-minute slot today. Move something, or widen your day in Settings.`
                    : `${choices.length} place${choices.length === 1 ? '' : 's'} “${fitting.title}” (${fitting.durationMinutes}m) would fit.`}{' '}
                <button className="link" onClick={() => setFitting(null)}>
                  cancel
                </button>
              </p>
            )}

            {/*
              A filter, once the list is long enough to need one.

              Phase 61 made the shortlist include the inbox, which on a loaded account took it from
              a handful to everything open — so the list this sits above is meaningfully longer
              than it was. Below the threshold a search box is the chrome P3 was spent deleting.

              Client-side: the rows are already here, so filtering them costs no request. It
              matches label names as well as titles, the same rule the Tasks filter follows, so
              typing `work` finds work labelled `work` on both screens.
            */}
            {shortlist.length > SHORTLIST_FILTER_AT && (
              <div className="shortlist-filter">
                <input
                  type="search"
                  value={shortlistQuery}
                  placeholder={`Filter ${shortlist.length} tasks`}
                  aria-label="Filter the shortlist"
                  onChange={(e) => setShortlistQuery(e.target.value)}
                />
                {hiddenByFilter > 0 && (
                  <span className="subtext">
                    {hiddenByFilter} hidden
                    <button className="link" onClick={() => setShortlistQuery('')}>
                      clear
                    </button>
                  </span>
                )}
              </div>
            )}

            {/*
              "Nothing to plan", not "Nothing waiting".

              The Waiting rail two hundred pixels above this line now uses that word for a promise
              somebody outside the app is holding, and the two states co-occur exactly: a day whose
              only open work is waiting on Acme has an empty shortlist *and* a full rail, so the
              screen read "Waiting on 2 things" over "Nothing waiting." That is the cost
              `01a059c6-bd68` records, arriving from the other direction — one word for two facts
              rather than two words for one.

              This was the one collision that rendered on a single screen, so it was fixed with
              the rail. Phase 72 finished the split: the blocker sense reads "Blocked by …" and
              GoalsToday reads "On the shortlist:", so "waiting" now names the promise only.
            */}
            {shortlist.length === 0 && <p className="muted">Nothing to plan.</p>}
            {shortlist.length > 0 && visibleShortlist.length === 0 && (
              <p className="muted">Nothing in the shortlist matches that.</p>
            )}
            {/*
              The first two groups, which are the shortlist.

              A heading each, with its count in the Board's column form (`.pill`): the rows under
              it after the search box, which is what the reader can check. It re-counts no other
              figure on the screen. The late band counts `byStatus.late` over `mine` — late started
              rows and late rows the shortlist withholds, not this group. The Board's sentence counts
              every `in_progress` row and is silent at three or fewer, so without this figure a
              small started pile had no count anywhere on Today (`01a15e3c-3b4c`). Where that
              sentence's rows are not all under *Started*, the heading names the rest rather than
              leave two figures over one word. An empty group draws nothing rather than a heading
              over nothing — except *Started 0 + 4 blocked*, the one empty heading that is not over
              nothing: without it the Board's sentence would count a pile Today never names.
            */}
            {(['started', 'dueOrPlanned'] as const).map(
              (id) =>
                (visibleGroups[id].length > 0 ||
                  (id === 'started' &&
                    groups.started.length === 0 &&
                    startedElsewhere !== null)) && (
                  <section
                    key={id}
                    className="shortlist-group"
                    data-testid="shortlist-group"
                    data-group={id}
                  >
                    <h4 className="shortlist-group-head" data-testid="shortlist-group-head">
                      {id === 'started' ? 'Started' : 'Due or planned'}{' '}
                      <span className="pill">{visibleGroups[id].length}</span>
                      {id === 'started' && startedElsewhere !== null && (
                        <span className="subtext" data-testid="started-elsewhere">
                          {' '}
                          + {startedElsewhere}
                        </span>
                      )}
                    </h4>
                    <ul className="shortlist">{visibleGroups[id].map(shortlistRow)}</ul>
                  </section>
                ),
            )}

            {/*
              The sentence at the foot, and the one action that answers it.

              Only when there is actually an overflow, and only ever an offer: pressing nothing
              leaves every row where it is. "The last N" is the rows under the fold, moved in one
              `bulkUpdateTasks` write — so it is one audit row and one undoable statement about
              the day rather than N separate ones.
            */}
            {plan.data && fold.below.size > 0 && (
              <p className="subtext budget-footer" data-testid="budget-footer">
                Over by{' '}
                <strong>
                  {spanLabel(plan.data.budget.shortlistedMinutes - plan.data.budget.freeMinutes)}
                </strong>
                .{' '}
                <button
                  className="link"
                  disabled={deferOverflow.isPending}
                  onClick={() => deferOverflow.mutate([...fold.below])}
                >
                  {deferOverflow.isPending
                    ? 'Moving…'
                    : /* "Tomorrow" only when the day on screen is actually today. The write is
                         `shiftDay(date, 1)` — a day after the day being *viewed* — so on any
                         other day the warm word would name a date the button does not write. */
                      `Move the last ${fold.below.size} to ${
                        date === todayIso(timezone) ? 'tomorrow' : 'the next day'
                      }`}
                </button>
              </p>
            )}

            {/*
              The third group: captured, never sorted — and not a shortlist, so it sits under the
              shortlist's footer rather than inside its arithmetic.

              Folded behind one row once it is longer than the batch it would offer. Thirty-four
              untouched captures were 70% of the ADHD persona's Today and buried the started work;
              the fold could not cut them, because a row with no estimate claims no minutes. So this
              is the cut, and it hides nothing for good: one press shows five, a second shows all,
              and typing in the filter opens the group, since a search is a request to see.
            */}
            {visibleGroups.captured.length > 0 &&
              (() => {
                const rows = visibleGroups.captured;
                const folds = shortlistQuery.trim() === '' && rows.length > CAPTURED_BATCH;
                const shown = !folds
                  ? rows
                  : capturedShown === 'all'
                    ? rows
                    : capturedShown === 'batch'
                      ? rows.slice(0, CAPTURED_BATCH)
                      : [];
                return (
                  <section
                    className="shortlist-group"
                    data-testid="shortlist-group"
                    data-group="captured"
                  >
                    <h4 className="shortlist-group-head" data-testid="shortlist-group-head">
                      {folds && capturedShown === 'none' ? (
                        <button
                          type="button"
                          className="link captured-toggle"
                          data-testid="captured-toggle"
                          aria-expanded="false"
                          onClick={() => setCapturedShown('batch')}
                        >
                          {rows.length} captured, never sorted — look at five?
                        </button>
                      ) : (
                        'Captured, never sorted'
                      )}
                    </h4>
                    {shown.length > 0 && <ul className="shortlist">{shown.map(shortlistRow)}</ul>}
                    {folds && capturedShown !== 'none' && (
                      <p className="subtext captured-more">
                        {capturedShown === 'batch' && (
                          <button
                            type="button"
                            className="link"
                            data-testid="captured-show-all"
                            onClick={() => setCapturedShown('all')}
                          >
                            Show the other {rows.length - CAPTURED_BATCH}
                          </button>
                        )}
                        <button
                          type="button"
                          className="link"
                          aria-expanded="true"
                          onClick={() => setCapturedShown('none')}
                        >
                          Fold them away
                        </button>
                      </p>
                    )}
                  </section>
                );
              })()}
          </aside>

          <div
            className="timeline"
            data-pane-id="timeline"
            data-testid="timeline"
            // The window is published so the end-to-end specs measure against the hours
            // actually drawn rather than assuming a fixed pair.
            data-start-hour={window.startHour}
            data-end-hour={window.endHour}
            data-px-per-hour={PX_PER_HOUR}
            style={{ height: (window.endHour - window.startHour) * PX_PER_HOUR }}
            onDragOver={onDragOver}
            onDragLeave={(e) => {
              // `dragleave` fires whenever the pointer crosses into a *child* — an hour
              // line, an existing block — not only when it leaves the timeline. Clearing
              // unconditionally made the preview flicker out as soon as the pointer moved
              // anywhere. `relatedTarget` is what is being entered, so if it is still
              // inside the timeline the drag has not left.
              if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
              setPreviewMinutes(null);
            }}
            onDrop={onDrop}
            /*
              Painting a selection, on the grid itself.

              Skipped when the gesture starts on something that already handles it — a block, a
              free-slot band, any control — because those own their own drags and a selection
              starting under them would fight them. Only the primary button draws.
            */
            onPointerDown={(e) => {
              if (e.button !== 0) return;
              if ((e.target as HTMLElement).closest('.block, .slot-band, button, a, input')) return;
              const bounds = e.currentTarget.getBoundingClientRect();
              const at = minutesAt(e.clientY - bounds.top);
              setPending(null);
              setDrawing({ from: at, to: at });
              e.currentTarget.setPointerCapture(e.pointerId);
            }}
            onPointerMove={(e) => {
              if (!drawing) return;
              const bounds = e.currentTarget.getBoundingClientRect();
              setDrawing({ from: drawing.from, to: minutesAt(e.clientY - bounds.top) });
            }}
            onPointerUp={(e) => {
              if (!drawing) return;
              e.currentTarget.releasePointerCapture(e.pointerId);
              const startMinutes = Math.min(drawing.from, drawing.to);
              const span = Math.abs(drawing.to - drawing.from);
              setDrawing(null);
              // A click is a selection of the default length, not a zero-minute block.
              setPending({
                startMinutes,
                durationMinutes: span >= SNAP_MINUTES ? span : DEFAULT_DRAWN_MINUTES,
              });
            }}
          >
            {hours.map((hour) => (
              <div
                key={hour}
                className="timeline-hour"
                style={{ top: (hour - window.startHour) * PX_PER_HOUR, height: PX_PER_HOUR }}
              >
                <span className="timeline-label">{String(hour).padStart(2, '0')}:00</span>
              </div>
            ))}

            {/*
              What time it is, and how much of the day has gone.

              The shade is a sibling of the hour rows and sits *under* the blocks, so it tints the
              empty grid without washing out any text — a scrim over the top would have taken the
              contrast of every block it covered with it. It also deliberately does not dim past
              blocks: a faded block reads as "done", and the honest statement about an unfinished
              one whose time has passed is not that.

              `pointer-events: none` on both, so neither becomes a drop target and swallows the
              drop it is describing (the same mistake as I-79).
            */}
            {marker && (
              <>
                <div className="timeline-past" style={{ height: marker.top }} aria-hidden="true" />
                <div
                  className="now-line"
                  data-testid="now-line"
                  data-now-minutes={Math.round(marker.minutes)}
                  style={{ top: marker.top }}
                  role="separator"
                  aria-label={`Now, ${minuteLabel(Math.round(marker.minutes))}`}
                >
                  <span className="now-label">{minuteLabel(Math.round(marker.minutes))}</span>
                </div>
              </>
            )}

            {previewMinutes !== null && dragging && (
              <div
                className="block-preview"
                data-testid="drop-preview"
                data-start-minutes={previewMinutes}
                style={{
                  top: ((previewMinutes - window.startHour * 60) / 60) * PX_PER_HOUR,
                  height: Math.max(18, (dragging.durationMinutes / 60) * PX_PER_HOUR),
                }}
              >
                <span className="block-time">{minuteLabel(previewMinutes)}</span> {dragging.title}
              </div>
            )}

            {/*
              Where it would fit. Rendered under the blocks, and each one is a button, so
              placing a task is a single click on an answer rather than a drag to a
              coordinate you have to work out.
            */}
            {fitting &&
              choices.map((slot) => {
                const startMinutes = minutesFromDayStart(slot.start, plan.data.dayStart);
                const top = ((startMinutes - window.startHour * 60) / 60) * PX_PER_HOUR;
                return (
                  <button
                    key={slot.start}
                    type="button"
                    className={`slot-band${slot.preferred ? ' is-preferred' : ''}`}
                    data-testid="free-slot"
                    data-start-minutes={startMinutes}
                    style={{
                      top,
                      height: Math.max(16, (fitting.durationMinutes / 60) * PX_PER_HOUR),
                    }}
                    title={
                      slot.preferenceRank === null || slot.preferenceRank === undefined
                        ? `Put “${fitting.title}” here — outside the times this usually happens`
                        : `Put “${fitting.title}” here — ${ordinal(slot.preferenceRank + 1)} choice`
                    }
                    onClick={() => takeSlot(slot)}
                  >
                    {minuteLabel(startMinutes)}
                    {/*
                      Which window this slot came from.

                      A template's windows are an ordered preference — "07:00–09:00, and failing
                      that 18:00–20:00" — and until now every preferred slot looked alike, so an
                      evening offered because the morning was full was indistinguishable from an
                      evening that was the first choice all along. Rank is 0-based; people count
                      choices from one.
                    */}
                    {typeof slot.preferenceRank === 'number' && slot.preferenceRank > 0 && (
                      <span className="slot-rank" data-testid="slot-rank">
                        {ordinal(slot.preferenceRank + 1)} choice
                      </span>
                    )}
                  </button>
                );
              })}

            {/*
              The selection being painted, and the one waiting for a title.

              Drawn as a block so it reads as what it will become, but dashed and captionless —
              nothing has been written, and it must not look like it has.
            */}
            {(drawing || pending) &&
              (() => {
                const startMinutes = drawing
                  ? Math.min(drawing.from, drawing.to)
                  : pending!.startMinutes;
                const minutes = drawing
                  ? Math.max(Math.abs(drawing.to - drawing.from), SNAP_MINUTES)
                  : pending!.durationMinutes;
                return (
                  <div
                    className="block block-drawn"
                    data-testid="drawn-block"
                    data-start-minutes={startMinutes}
                    data-duration-minutes={minutes}
                    style={{
                      top: ((startMinutes - window.startHour * 60) / 60) * PX_PER_HOUR,
                      height: Math.max(14, (minutes / 60) * PX_PER_HOUR),
                    }}
                  >
                    <span className="block-time">{minuteLabel(startMinutes)}</span>{' '}
                    <span className="subtext">{minutes}m</span>
                  </div>
                );
              })()}

            {plan.data.events.map((event) => (
              <BlockView
                key={event.id}
                sitting={sittingOf.get(event.id) ?? null}
                startHour={window.startHour}
                endHour={window.endHour}
                selected={selectedBlock === event.id}
                onSelect={() =>
                  setSelectedBlock((current) => (current === event.id ? null : event.id))
                }
                placement={placements.get(event.id)}
                onDragStart={(moved, durationMinutes) =>
                  setDragging({ eventId: moved.id, title: moved.title, durationMinutes })
                }
                onDragEnd={() => {
                  setDragging(null);
                  setPreviewMinutes(null);
                }}
                event={event}
                dayStart={plan.data.dayStart}
                timezone={plan.data.timezone}
                onOpenTask={onOpenTask}
                onDelete={() => remove.mutate(event.id)}
                onComplete={() => event.taskId && finish.mutate(event.taskId)}
                onResize={(minutes, updateEstimate) =>
                  resize.mutate({ eventId: event.id, durationMinutes: minutes, updateEstimate })
                }
                goalClass={event.taskId ? (blockGoalClass.get(event.taskId) ?? null) : null}
              />
            ))}
          </div>

          {/*
            Actions for the selected block, with room to be tappable. The controls on the block
            itself are 11px wide and its resize strip 7px tall — usable with a cursor, hopeless
            with a thumb — and on touch there is no drag to fall back on at all.

            Below the timeline rather than floating over it: as a sticky bar inside it, this
            covered the lower hours and swallowed clicks on the very slots "Move to…" had just
            offered.
          */}
          {selectedBlock !== null &&
            (() => {
              const event = plan.data.events.find((e) => e.id === selectedBlock);
              if (!event) return null;
              const minutes =
                (new Date(event.endsAt).getTime() - new Date(event.startsAt).getTime()) / 60_000;
              return (
                <div className="block-bar" data-testid="block-actions">
                  <span className="block-bar-title">
                    {clockLabel(event.startsAt, plan.data.timezone)} {event.title}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setFitting({
                        eventId: event.id,
                        title: event.title,
                        durationMinutes: minutes,
                      })
                    }
                  >
                    Move to…
                  </button>
                  {/*
                    Inert until the screen reflects the last change.
                    
                    Each click computes from the *rendered* duration, so a tap landing before the
                    refetch compounds off a stale value: three −15m taps shrank a 60 minute block
                    to 30 rather than 15. Guarding on `resize.isPending` alone was not enough —
                    that clears when the mutation resolves, while the day plan is still being
                    refetched — so the plan's own fetching state is part of the condition.
                  */}
                  <button
                    type="button"
                    aria-label="Fifteen minutes shorter"
                    disabled={minutes <= 15 || resize.isPending || plan.isFetching}
                    onClick={() =>
                      resize.mutate({
                        eventId: event.id,
                        durationMinutes: Math.max(15, minutes - 15),
                        updateEstimate: false,
                      })
                    }
                  >
                    −15m
                  </button>
                  <span className="subtext">{minutes}m</span>
                  <button
                    type="button"
                    aria-label="Fifteen minutes longer"
                    disabled={resize.isPending || plan.isFetching}
                    onClick={() =>
                      resize.mutate({
                        eventId: event.id,
                        durationMinutes: minutes + 15,
                        updateEstimate: false,
                      })
                    }
                  >
                    +15m
                  </button>
                  {event.taskId && event.completedAt === null && (
                    <button
                      type="button"
                      onClick={() => {
                        finish.mutate(event.taskId!);
                        setSelectedBlock(null);
                      }}
                    >
                      {/* The same act as the panel's button and the bar's, and it said `Done`
                          while its own ✓ beside it was titled "Complete — the block stays". */}
                      {STATUS_VERB.done.verb}
                    </button>
                  )}
                  {/*
                    Into the doing screen, with this block in hand.

                    Offered on any block rather than only the one running now. A sweep of every
                    persona account on 2026-09-10 found 38 blocks across 61 days and **none on
                    today**, so a control that appeared only during a running block would be a
                    door nobody could open. From a block still to come the screen says how long
                    until it starts, which is the state it will usually be in.
                  */}
                  <button type="button" onClick={() => onOpenNow(event.id)}>
                    Now
                  </button>
                  <button
                    type="button"
                    className="danger"
                    onClick={() => {
                      remove.mutate(event.id);
                      setSelectedBlock(null);
                    }}
                  >
                    Remove
                  </button>
                  <button type="button" className="link" onClick={() => setSelectedBlock(null)}>
                    close
                  </button>
                </div>
              );
            })()}

          {plan.data.habits.length > 0 && (
            <aside className="pane" data-pane-id="habits">
              {/*
              How much of the day's protocol is done.

              `/api/review/month` has computed a per-day `habitsMet` since Phase 6 and the month
              grid clamped it to three dots, so the number existed and was never legible. Here it
              is read straight off the plan the screen already has — one figure that answers "did
              I do today", which at 24 habits is the only way the pane is readable at all.

              It counted `onTrack`, which is not that question. Every weekly habit is trivially on
              track on a Wednesday morning, so the header read "8 of 11" to somebody who had logged
              four things — and a sleep habit sitting at 0 of 7.5 wore a green pill. It then counted
              `loggedOnDay > 0`, which is "logged anything": 84 of 90 minutes counted, and a ceiling
              kept clean never could. It counts the rows whose verdict is kept now — `doneOnDay`.
            */}
              <h3>
                Habits
                {/*
                Labelled, because the denominator is not the number of rows below it. The count is
                over what is still work on the day shown — every daily habit, plus periodic ones
                short of where they need to be — while the list renders everything you track. With
                31 habits that read as "2/26" above 31 rows, and was reported twice as a wrong
                number when it was an unlabelled one.

                And the label names the day, because it stopped always being today. It said "due
                today" on every date the arrows could reach, over figures that were today's — the
                display was as wrong as the word. The figures are the day's now; the word has to
                follow, or the sentence goes on being false in the other direction.
              */}
                {countedOnDay.length > 0 && (
                  <span className="pane-count" data-testid="habits-met-count">
                    {' '}
                    {doneOnDay}/{countedOnDay.length} due{' '}
                    {date === todayIso(timezone) ? 'today' : readableDay(date)}
                  </span>
                )}
              </h3>
              {/* The key to the strips below — the same component the Habits screen draws. */}
              {habitPane.rows.some((h) => h.strip.length > 0) && <StripLegend />}
              <ul className="day-habits">
                {habitPane.rows.map((h) => (
                  <DayHabitRow
                    key={h.id}
                    habit={h}
                    date={date}
                    today={todayIso(timezone)}
                    /* Still listed — a met habit is one you may well log again — but visibly not
                     part of the count above, so the header and the list stop disagreeing. */
                    notDue={!countedOnDay.some((d) => d.id === h.id)}
                    onActed={holdHabit}
                  />
                ))}
              </ul>
              {/*
                Counted, so nothing is missing without saying so — the same shape as the goals
                pane's "Show all". Not due is not gone: a met habit is one you may log again.
              */}
              {habitPane.notDueCount > 0 && (
                <button
                  type="button"
                  className="link"
                  data-testid="day-habits-more"
                  aria-expanded={!habitPane.collapsed}
                  onClick={() => setShowNotDue((open) => !open)}
                >
                  {habitPane.collapsed
                    ? `Show ${habitPane.notDueCount} more, not due ${date === todayIso(timezone) ? 'today' : readableDay(date)}`
                    : `Hide the ${habitPane.notDueCount} not due`}
                </button>
              )}
            </aside>
          )}
        </div>
      )}

      {/* After the plan, not before it: this is a check on what you have just decided. */}
      {plan.data && (
        <GoalsToday
          scheduledTasks={plan.data.tasks}
          candidates={plan.data.candidates}
          habits={plan.data.habits}
          onOpenTask={onOpenTask}
        />
      )}

      {/*
        The title for a selection that has been drawn but not yet made.

        This is capture, with a time attached — the same component the header and the Tasks screen
        use, so a task drawn on the calendar carries the same goal, labels and priority row and is
        the same kind of thing. Escape closes it and the selection goes with it, having written
        nothing.
      */}
      {pending && plan.data && (
        <div className="drawn-capture card" data-testid="drawn-capture">
          <div className="row gap">
            <strong>{minuteLabel(pending.startMinutes)}</strong>
            <span className="subtext">
              {pending.durationMinutes}m ·{' '}
              {minuteLabel(pending.startMinutes + pending.durationMinutes)}
            </span>
            <button type="button" className="link" onClick={() => setPending(null)}>
              cancel
            </button>
          </div>
          <Capture
            compact
            autoFocusInput
            onCreated={(created) => {
              // `minutesAt` counts from local midnight, which is what `dayStart` is.
              const startsAt = new Date(
                new Date(plan.data!.dayStart).getTime() + pending.startMinutes * 60_000,
              ).toISOString();
              placeDrawn.mutate({
                taskId: created.id,
                startsAt,
                durationMinutes: pending.durationMinutes,
              });
            }}
          />
        </div>
      )}

      <TaskMenu target={menu.target} onClose={menu.close} onOpenTask={onOpenTask} />
    </section>
  );
}

/**
 * One habit, loggable where you are already standing.
 *
 * The pane used to be read-only, so recording a gym session meant leaving the day,
 * finding the habit on another screen, pressing +1 and navigating back. The loop that
 * habit tracking depends on is "did it, tick it" — anything longer than one tap is a loop
 * that gets skipped, and a habit tracker nobody logs to is worse than none, because it
 * reports zero and you learn to distrust it.
 *
 * The period strip is the second half: a bare 2/3 says nothing about *when* those two
 * happened, and three sessions crammed into a Sunday is not the same week as three spread
 * out, even though the count is identical.
 */
function DayHabitRow({
  habit,
  date,
  today,
  notDue,
  onActed,
}: {
  habit: DayHabit;
  date: string;
  /** The account's today, so a reading's day is a distance on today and a date on a past day. */
  today: string;
  /** Rendered, but outside the header's count. */
  notDue?: boolean;
  /** A log or an undo landed — so the pane can hold this row where it is. See `useLogHold`. */
  onActed?: (habitId: string) => void;
}) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => {
    setError(null);
    return queryClient.invalidateQueries();
  };
  const onError = (e: unknown) =>
    setError(e instanceof ApiError ? e.message : 'Could not record that.');

  /*
    Held before the refetch, so the arrangement captured is the one on screen. An undo too: taking
    back a done row's entry ranks it a tier *up*, which moves rows under the thumb just the same.
  */
  const acted = () => {
    onActed?.(habit.id);
    return invalidate();
  };

  const log = useMutation({
    mutationFn: (value: number) => logHabit(habit.id, { value, occurredOn: date }),
    onSuccess: acted,
    onError,
  });

  const undo = useMutation({
    mutationFn: () => undoHabitLog(habit.id, date),
    onSuccess: acted,
    onError,
  });

  /*
    A reading is not a score out of a target.

    `78.84/78` on a body-weight row reads as a fraction complete, and the pill beside it went
    green or red on `onTrack` — which for a ceiling means "at or under", so the row was red every
    single morning of a successful cut. The reading and its unit say more, and say it without
    implying the weight is 101% of a weight.
  */
  // Null on a reading, which has no run — keyed on `DayHabit.isReading`, said by the service.
  const run = runFigure(habit, habit.period);
  /*
    Today paged back to a day already over on the account's calendar is judged as that day (Phase
    87), so the server's verdict is the right one — and its word was not: *behind* and a cell
    *still running* over a day nobody can live again (`01a15a31-5ea4`). `periodIsOver` asks the
    account's today, and the row and its strip then say the verdict as a result. The log control
    below is untouched, so a backfill still turns *missed* into *met*.
  */
  const periodOver = periodIsOver(habit.byDay, today);
  const word = verdictWord(habit, { period: habit.period, asOf: date, today, periodOver });

  return (
    <li
      className={notDue ? 'day-habit is-not-due' : 'day-habit'}
      data-testid="day-habit"
      data-habit-name={habit.name}
    >
      <div className="row gap">
        <span
          /*
            Was `verdict === 'over' && isMarginalBreach(habit) ? 'warn' : verdictTone(...)`: a
            local repair of the colour, because the verdict said "over" about a gram past the
            ceiling and about double. It fixed the pill and not the word beside it — the `title`
            below still read "over" — which is the half that reaches a screen reader. The
            distinction is now in the verdict itself, so both come from the one vocabulary.
          */
          className={`pill ${verdictTone(habit.verdict)}`}
          data-testid="day-habit-count"
          data-verdict={habit.verdict}
          /*
            The word *and* the period. The word is also on the row now, so this is no longer the
            only place it is said — but the period is still said nowhere else, and "0/20" on a
            weekly habit beside "0/3" on a daily one is two different questions in one column.
          */
          title={`${word} · ${habit.period}`}
        >
          {/*
            The figure — or, for abstinence, the word, because abstinence has no figure. "0/0" is
            not a fact about a limit of zero, so the pill carries the verdict instead and the row
            does not repeat it below.

            This used to print `habit.state`, the raw enum, so a broken abstinence habit said
            "breached" — a word in no vocabulary the app has ever agreed, on the one row that
            already had something to say.
          */}
          {/*
            The short form of the one figure — see `habitFigure`. A reading is its last reading
            and, unless it was taken on the day shown, its day; never `actual`, which is 0 for a
            period nobody read and the week's mean for a weekly `mean` ceiling.
          */}
          {habitFigure(habit, {
            window: habit.period,
            asOf: date,
            today,
            short: true,
          }) ?? word}
        </span>
        <span className="day-habit-name">
          {habit.icon ? `${habit.icon} ` : ''}
          {habit.name}
        </span>
        {/*
          The verdict, in words, on every row that has a figure.

          It was rendered for a *reading* only, and from `stateLabel` — the other vocabulary — so
          two thirds of the health nut's thirty-one habits were a coloured pill and no words at
          all, and the third that did get a word got "on track" for a habit with nothing logged.
          The verdict itself was in the `title`: hover-only on a desktop and unreachable on a
          phone, which is where the habits actually get logged.
        */}
        {!habit.isAbstinence && (
          <span className="subtext" data-testid="day-habit-state">
            {/* How long a reading has gone unread is the day beside its figure in the pill; how long
                since one was *taken*, once that is longer than its period, is this word. */}
            {word}
          </span>
        )}
        {/*
          The streak, where the logging happens.

          It lived only in `GET /api/habits/{id}/history` — a request made only when a card on
          another screen is expanded — so the number most likely to make somebody log the thing
          was the one number the logging screen did not have.
        */}
        {/*
          At zero too. It was drawn only while a run was alive, so a habit whose run had just broken
          and one that was never a count of anything looked the same here, while the Habits card
          said *no run* and the tile behind it *best 12* — three answers to one question
          (`01a03813-f7d8`). `0 days in a row` is the card's words; the best stays in the `title`,
          said as the card says it, because this row is the narrowest that carries a run.
        */}
        {run && (
          <span
            className="pill"
            data-testid="day-habit-run"
            title={run.best ? `${run.run} · ${run.best}` : undefined}
          >
            {/* "2 in a row" left out what two *were* — on a weekly habit it means two weeks. The
                habits screen has always said so; this now uses the same words. */}
            {run.run}
          </span>
        )}
      </div>

      <PeriodStrip
        strip={habit.strip}
        name={habit.name}
        period={habit.period}
        polarity={habit.polarity}
        today={date}
        periodOver={periodOver}
        target={habit.target}
        unit={habit.unit}
        reading={habit.isReading}
      />

      <div className="row gap day-habit-actions">
        {/* The shared control, so this screen and the habits screen cannot disagree again about
            what pressing the button records. */}
        <LogHabit
          habit={habit}
          pending={log.isPending}
          onLog={(value) => log.mutate(value)}
          // Only when there is something to take back.
          // An entry, not a positive total: `clean` logs 0 and is as takeable-back as any other.
          onUndo={habit.recordedOnDay ? () => undo.mutate() : undefined}
          undoPending={undo.isPending}
        />
      </div>

      {error && <p className="error subtext">{error}</p>}
    </li>
  );
}

/*
  Two tones were not enough for a ceiling.

  `onTrack` is false the instant a limit is passed, so a row 0.84 kg over a ceiling of 80 wore the
  same red as one at double it, and wore it every morning until the period rolled. A breach inside
  `LIMIT_MARGIN` is amber and says "just over" — the pill's `title` is the only place this row
  spells the state out, so the word has to move too and not just the colour.
*/

function BlockView({
  sitting,
  startHour,
  endHour,
  selected,
  onSelect,
  placement,
  onDragStart,
  onDragEnd,
  event,
  dayStart,
  timezone,
  onOpenTask,
  onDelete,
  onComplete,
  onResize,
  goalClass,
}: {
  /** Which of this task's blocks today, when there is more than one. */
  sitting: { index: number; total: number } | null;
  startHour: number;
  endHour: number;
  /** The colour class of the goal this block's task serves, if it serves one. */
  goalClass: string | null;
  /** Whether this block's actions are open below the timeline. */
  selected: boolean;
  onSelect: () => void;
  placement: { column: number; columns: number } | undefined;
  /** Told to the parent so the drag preview can be drawn at the right height. */
  onDragStart: (event: TimelineEvent, durationMinutes: number) => void;
  onDragEnd: () => void;
  event: TimelineEvent;
  dayStart: string;
  timezone: string;
  onOpenTask: (id: string) => void;
  onDelete: () => void;
  onComplete: () => void;
  onResize: (minutes: number, updateEstimate: boolean) => void;
}) {
  const startMinutes = minutesFromDayStart(event.startsAt, dayStart);
  const durationMinutes =
    (new Date(event.endsAt).getTime() - new Date(event.startsAt).getTime()) / 60_000;

  const top = ((startMinutes - startHour * 60) / 60) * PX_PER_HOUR;
  // Clamped to the end of the drawn day. A block running past midnight belongs partly to
  // tomorrow, and tomorrow's timeline shows the rest of it; without this the overflow is
  // simply cut off by the container with nothing to say it happened.
  const timelineHeight = (endHour - startHour) * PX_PER_HOUR;
  const height = Math.max(18, Math.min((durationMinutes / 60) * PX_PER_HOUR, timelineHeight - top));
  const done = event.completedAt !== null;
  /*
    Deleted in Google, and still here on purpose.

    The server keeps such an entry on the day for a week (`DELETED_EVENT_GRACE_DAYS`) so that the
    deletion is something you watch happen rather than something you discover — "a silent vanishing
    is indistinguishable from a sync bug". It kept it and the response schema dropped `deletedAt`,
    so this block drew a deleted meeting exactly like a live one and the grace window bought
    nothing at all.

    Drawn as *dashed and struck*, not merely struck: `is-done` already owns plain strike-through,
    and "you finished this" and "this is no longer in your calendar" must not look the same. The
    badge carries the word, because the two treatments still have to survive being described.
  */
  const removed = event.deletedAt !== null;
  const goalColorClass_ = event.blockType === 'task_block' ? goalClass : null;
  const column = placement?.column ?? 0;
  const columns = placement?.columns ?? 1;

  return (
    <div
      /*
        A task block takes the colour of the goal it serves; a calendar event stays neutral grey,
        so "mine" and "the world's" remain distinguishable without spending a hue on it. This is
        what turns the day into a picture of *which goal the day went to*.
      */
      className={`block block-${event.blockType} ${goalColorClass_ ?? ''} ${
        done ? 'is-done' : ''
      } ${removed ? 'is-removed' : ''} ${selected ? 'is-selected' : ''}`}
      // The end-to-end drag specs need to assert *where* a block landed and how tall it
      // is, which are the two things this component computes. Reading them back off the
      // element is more honest than re-deriving them in the test.
      data-testid="block"
      data-block-title={event.title}
      data-removed={removed ? 'true' : undefined}
      data-start-minutes={startMinutes}
      data-duration-minutes={durationMinutes}
      // Columns come from `layoutOverlaps`; the gutter on the left is where the hour
      // labels live, so the usable width starts after it.
      style={{
        top,
        height,
        left: `calc(3.4rem + ${(column / columns) * 100}% - ${((column / columns) * 3.9).toFixed(3)}rem)`,
        width: `calc(${(100 / columns).toFixed(4)}% - ${(3.9 / columns + 0.1).toFixed(3)}rem)`,
      }}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('planner/event', event.id);
        onDragStart(event, durationMinutes);
      }}
      onDragEnd={onDragEnd}
    >
      {/*
        Clicking the body opens the actions — a convenience for pointers, with no `role` or
        `tabIndex`. It had both at first, which nested a button inside a `role="button"`: invalid
        ARIA, ambiguous to a screen reader, and two controls whose accessible names overlapped.
        Keyboard and assistive access go through the explicit ⋯ control below instead.
      */}
      <div
        className="block-body"
        onClick={(e) => {
          // Not when the title link or an action was the actual target.
          if ((e.target as HTMLElement).closest('button')) return;
          onSelect();
        }}
      >
        <span className="block-time">{clockLabel(event.startsAt, timezone)}</span>{' '}
        {event.taskId ? (
          <button className="link" onClick={() => onOpenTask(event.taskId!)}>
            {event.title}
          </button>
        ) : (
          <span>{event.title}</span>
        )}
        {/*
          Which sitting this is, when the task has more than one today.

          Two blocks of the same task were otherwise indistinguishable — the same title twice, with
          nothing saying they were halves of one thing rather than a duplicate made by accident.
        */}
        {sitting && (
          <span className="block-sitting" data-testid="block-sitting">
            {sitting.index} of {sitting.total}
          </span>
        )}
        {removed && (
          <span
            className="block-removed"
            data-testid="block-removed"
            title="Deleted in your calendar. Shown here for a week so the deletion is visible, then it goes."
          >
            deleted
          </span>
        )}
      </div>

      <div className="block-actions">
        <button
          className="link block-more"
          aria-label={`Actions for ${event.title}`}
          aria-expanded={selected}
          onClick={onSelect}
        >
          ⋯
        </button>
        {event.taskId && !done && (
          <button className="link" onClick={onComplete} title="Complete — the block stays">
            ✓
          </button>
        )}
        <button className="link danger" onClick={onDelete} title="Remove the block">
          ×
        </button>
      </div>

      {/* Resize writes to the event only; the estimate changes only if asked. */}
      <button
        className="block-resize"
        title="Resize (hold Shift to update the task estimate too)"
        onClick={(e) => onResize(durationMinutes + 15, e.shiftKey)}
        onContextMenu={(e) => {
          e.preventDefault();
          onResize(Math.max(15, durationMinutes - 15), e.shiftKey);
        }}
        aria-label="Resize block"
      />
    </div>
  );
}
